export default {
  paytable: 'Paytable',
  rules: 'Game Rules',

  /**
   * 玩家看得到的错误文案（`common.errors.*`）。
   *
   * ⚠️ 不要把 `ApiError.message` 直接设进画面 state —— 那是后端原文，或 http.ts 产生的
   * `'Request timed out'` / `'Unauthorized'` / `'HTTP 502'` 这类给工程师看的英文。
   * 转换统一走 `src/api/errorMessage.ts` 的 `toUserMessage()`。
   *
   * ⚠️ 这个 namespace 必须放在**本档**。`src/locales/index.ts` 是
   * `{ ...enMain, common: enCommon }` —— `common` 会整个被本档覆盖，
   * 所以写在 auto-generated 的 `src/locales/en.ts` 里的 `common.*` 一律取不到。
   */
  /** Status copy for the operator "board replay" root (src/replay/ResultDetailApp.vue). */
  replay: {
    loading: 'Loading result…',
    /** gameId not in REGISTERED_GAME_IDS — a product limit, not a transient error */
    unsupported: 'Board replay is not supported for this game yet',
    loadFailed: 'Failed to load the result',
    missingParams: 'The replay link is missing required parameters (record_id / token / gameId)',
    /** Appended after loadFailed when the game is registered but its chunk failed to load */
    refreshHint: 'Please refresh the page',
  },

  /**
   * Slow-network soft-timeout dialog (`SlowNetworkDialog.vue`).
   * The timeout never interrupts the player — it only offers Refresh vs Wait.
   * Copy matches the competitor verbatim (2026-08-18).
   */
  slowNetwork: {
    message: 'Your Internet seems a bit slow, would you like to Refresh or continue Waiting?',
    refresh: 'Refresh',
    wait: 'Wait',
  },
  /** New host loading mask (docs/LOADING_PROTOCOL.md): phase text + fake progress + start button. */
  /** New host loading mask. Copy taken verbatim from the competitor (2026-08-20). */
  loading: {
    phase: {
      login: 'Logging in',
      game: 'Reading Info',
      /** only this phase carries the percentage */
      assets: 'Loading resources',
      complete: 'Complete',
    },
    percent: '[{n}%]',
    tips: {
      device: 'Update to latest device update for best gaming experience!',
      idlePages: 'Turn off idle pages, give me more resources!',
      wifi: 'Downloading over Wi-Fi recommended for HD gaming!',
      browser: 'For the ultimate game experience, use Safari or Chrome!',
      cache: 'Previously loaded games load faster from browser cache!',
      firstLoad: 'If this is your first visit, downloading may take a while.',
    },
    /** competitor `#get-started-text` 逐字 */
    start: 'GET STARTED',
  },

  errors: {
    // 依错误分类（errorMessage.ts 的 ErrorKind）
    timeout: 'Connection timed out. Please try again.',
    network: 'Network error. Please check your connection and try again.',
    server: 'The server is busy. Please try again shortly.',
    loadFailed: 'Failed to load. Please try again.',
    // 登录 / 认证流程
    missingTicket: 'Missing ticket. Please re-enter the game.',
    loginFailed: 'Login failed. Please re-enter the game.',
    ticketInvalid: 'Your login is no longer valid. Please re-enter the game.',
    verifyFailed: 'Verification failed. Please re-enter the game.',
    sessionExpired: 'Your session has expired. Please close this page and re-enter the game.',
    // 余额弹窗（BalanceModal）
    fetchFailed: 'Failed to fetch balance, please retry.',
    missingLogin: 'Missing login information',
    networkError: 'Network error',
    tokenExpired: 'Login expired',
  },

  history: {
    chooseDateRange: 'Select Date Range',
    title: 'Game History',
    today: 'Today',
    last7days: 'Last 7 days',
    custom: 'Custom',
    start: 'Start',
    end: 'End',
    confirm: 'Confirm',
    winInfo: 'Payout',
    noWin: 'No Winning Combination',
    /** {count}-of-a-Kind */
    xOfAKind: '{count} of a Kind',
    /** {count} winning paths */
    way: '{count} way(s)',
    /** Win-both-ways games (ryxc): this award is counted left-to-right */
    wayLeftToRight: 'Left-to-Right: {count} way(s)',
    /** Win-both-ways games (ryxc): this award is counted right-to-left */
    wayRightToLeft: 'Right-to-Left: {count} way(s)',
    /** Cash Prize（JQT Pay Anywhere 純獎金總額） */
    cashPrize: 'Cash Prize',
    /** (i) tooltip formula label */
    formulaLabel: 'Bet Size x Bet Level x Symbol Payout Values x Way(s)',
    /** (i) formula label for line games (JQT)：無 Way(s)，賠付值已含連線數量檔位 */
    formulaLabelLine: 'Bet Size x Bet Level x Symbol Payout Values',
    // ── MULTIPLIER variant ───────────────────────────
    bonusMultiplier: 'Win Multiplier',
    /** 倍数 +N（FG 本轉新增倍率） */
    multiplierAdded: 'Multiplier',
    /** 奖金倍数 +N（翻倍夺宝：scatter row 右側；JSMJT） */
    multiplierGain: 'Win Multiplier +{n}',
    /** (i)奖金倍数 */
    bonusMultiplierTooltip: 'Multiplier',
    multiplierWin: 'Multiplier Win',
    totalWinAccrued: 'Total Win',
    /** Total Win（short form of `totalWinAccrued`; zh differs: 共赢得 vs 共赢得奖金） */
    totalWin: 'Total Win',
    /** Multiplier Activated ×N（ANBS：某欄激活且含阿努比斯時顯示） */
    multiplierActivated: 'Multiplier Activated',
    /** collected（zcm：收集 N 隻招财猫 badge 副標） */
    collected: 'collected',
    // ── SCATTER / FREE_SPIN variant ──────────────────
    /** {count} Free Spins */
    freeSpinAwarded: '{count} Free Spins',
    /** +{count} Free Spins：FG retrigger(KLDG +1) */
    freeSpinRetrigger: '+{count} Free Spins',
    // ── DetailHeader title ───────────────────────────
    normalSpin: 'Normal Spin',
    freeSpin: 'Free Spin',
    /**
     * Free Spin {n}/{total} — default FG group header / dropdown label.
     * Per-game overrides go in content.ts → history.labels.freeSpin (inline I18nText);
     * no need to add a new key here.
     */
    freeSpinIndex: 'Free Spin: {n}/{total}',
    /** Dropdown bottom close button */
    close: 'Close',
    /** HistoryTable empty state */
    noRecords: 'No Game History',
    /** HistoryTable header: Time */
    time: 'Time',
    /** HistoryTable loading text */
    loading: 'Loading',
    /** HistoryTable footer: {count} records */
    recordsCount: '{count} Records',
    // ── StatsBar 4 columns ───────────────────────────
    transactionId: 'Transaction',
    /** vant toast shown after copying the transaction id (the label itself stays put) */
    copied: 'Copied',
    bet: 'Bet',
    profit: 'Profit',
    balance: 'Balance',
    // ── RoundTabs ────────────────────────────────────
    roundN: 'Round {n}/{total}',
    betSize: 'Bet Size',
    betMultiplier: 'Bet Level',
    fetchFailed: 'Failed to fetch records, please retry.',
  },
}
